import type { ReactNode } from "react";
import type { TocLink } from "@ekafyi/viteui-lib/utils/api-types";
import { formatDate } from "../../utils";
import { TableOfContents } from "../TableOfContents";
import { TitleBarScrollObsOuter } from "../TitleBar";
import * as styles from "./EntryDetail.css";
import { RelatedContent } from "./RelatedContent";

export interface EntryDetailProps {
  data: {
    title: string;
    createdAt: Date;
    updatedAt?: Date | null;
    excerpt?: string | null;
    gardenStatus?: string | null;
  };
  toc?: TocLink[];
  children?: ReactNode;
}

const SamplePostActions = () => {
  const handleSampleClick = () => { alert("clicked entry detail") }; // prettier-ignore
  return (
    <div className={`flex gap-2`}>
      <button className="w-10 h-10 flex items-center justify-center" onClick={handleSampleClick}>
        <svg width="24" height="24">
          <use xlinkHref={`/shared-assets/images/icons.svg#heart-outline`} />
        </svg>
      </button>
      <button className="w-10 h-10 flex items-center justify-center" onClick={handleSampleClick}>
        <svg width="24" height="24">
          <use xlinkHref={`/shared-assets/images/icons.svg#share`} />
        </svg>
      </button>
    </div>
  );
};

const SampleByline = (
  props: Pick<EntryDetailProps["data"], "createdAt" | "updatedAt" | "gardenStatus">
) => {
  const { createdAt, updatedAt, gardenStatus } = props;
  return (
    <div className="not-prose flex flex-col gap-x-4 gap-y-2">
      <div className="text-base">
        By{" "}
        <a
          className="text-e-color-onSurface font-bold border-b border-current hover:border-b-2 focus:border-b-2"
          href="/"
        >
          Eka (@ekafyi)
        </a>
      </div>
      <div className="text-xs flex gap-4">
        <p className={`text-e-color-onSurface/75`}>
          <time>{formatDate(createdAt)}</time>
          {updatedAt && (
            <>
              &nbsp;&middot;&nbsp;last updated <time>{formatDate(updatedAt)}</time>
            </>
          )}
        </p>
        {gardenStatus && (
          <p>
            🌱 <strong>{gardenStatus}</strong> (?)
          </p>
        )}
      </div>
    </div>
  );
};

export const EntryDetail = ({ data, toc = [], children = null }: EntryDetailProps) => {
  const { title, excerpt, createdAt, updatedAt, gardenStatus } = data;

  // TODO (later,perf) Lazy-load next/link for back button on visible.
  // Add ref={refBack} to leadingNode to detect visibility.
  // import { useInView } from "react-intersection-observer";
  // const { ref: refBack, inView: inViewBack } = useInView({ triggerOnce: true });

  const PROSE_CSS = `prose prose-sm md:prose-base prose-e`;

  return (
    <>
      <TitleBarScrollObsOuter
        leadingNode={
          // !! update back button here
          <div className={`w-10 h-10 lg:hidden -ml-2`}>
            <a className={`w-10 h-10 flex items-center justify-center`} href={`/notes`}>
              <svg width="24" height="24">
                <use xlinkHref={`/shared-assets/images/icons.svg#arrow-left`} />
              </svg>
            </a>
          </div>
        }
        trailingNode={<SamplePostActions />}
        initialContentNode={null}
        scrolledContentNode={<span className="lg:text-sm font-semibold">{title}</span>}
      />
      <main id="main" className={styles.outer}>
        <article className={toc.length ? styles.articleWithToc : styles.articleNoToc}>
          <header className={`${PROSE_CSS} space-y-5`}>
            <h1>{title}</h1>
            {excerpt && <p className="lead">{excerpt}</p>}
            {/* TODO (later) add featured image here */}
            {/* ... */}
            <SampleByline createdAt={createdAt} updatedAt={updatedAt} gardenStatus={gardenStatus} />
          </header>
          {!!toc.length && (
            <div className={`${styles.tocContainer} ${PROSE_CSS}`}>
              <div
                className={`not-prose xl:sticky xl:top-16 xl:max-h-screen xl:overflow-y-auto text-sm px-4 py-2 border border-e-color-onSurface xl:border-y-0 xl:border-r-0 xl:py-0 2xl:-ml-4 xl:border-e-color-outlineVariant`}
              >
                {!!toc?.length && <TableOfContents data={toc} />}
              </div>
            </div>
          )}
          <div className={PROSE_CSS}>{children}</div>
        </article>
        <div>
          <RelatedContent
            className={`${styles.related} bg-e-color-surfaceVariant/40 text-e-color-onSurfaceVariant`}
          />
        </div>
      </main>
    </>
  );
};
